// Minimaler, read-only XLSX-Leser (+ CSV) — bewusst ohne externe Dependency.
//
// Warum selbst gebaut: die einzige auf npm verfügbare SheetJS-Version (0.18.5)
// hat eine offene Prototype-Pollution-CVE, und der gepatchte Build kommt nur
// vom eigenen CDN (nicht registry-installierbar → riskant für den Pages-Build).
// Wir brauchen hier nur „Zellen als Text lesen", das ist überschaubar.
//
// Genutzte Browser-APIs: DecompressionStream('deflate-raw'), DOMParser.

export type Grid = string[][];

// ------------------------------------------------------------------
// ZIP (nur das Nötigste: Central Directory + stored/deflate)
// ------------------------------------------------------------------

interface ZipEntry {
  name: string;
  method: number;
  compressedSize: number;
  localOffset: number;
}

function findEocd(dv: DataView): number {
  // End of Central Directory: Signatur 0x06054b50, max. 64 KB Kommentar am Ende
  const start = Math.max(0, dv.byteLength - 65_557);
  for (let i = dv.byteLength - 22; i >= start; i--) {
    if (dv.getUint32(i, true) === 0x06054b50) return i;
  }
  return -1;
}

function readCentralDirectory(buf: ArrayBuffer): Map<string, ZipEntry> {
  const dv = new DataView(buf);
  const eocd = findEocd(dv);
  if (eocd < 0) throw new Error('Kein gültiges ZIP/XLSX (EOCD fehlt).');

  const count = dv.getUint16(eocd + 10, true);
  let p = dv.getUint32(eocd + 16, true);
  const dec = new TextDecoder('utf-8');
  const out = new Map<string, ZipEntry>();

  for (let i = 0; i < count; i++) {
    if (dv.getUint32(p, true) !== 0x02014b50) break;
    const method = dv.getUint16(p + 10, true);
    const compressedSize = dv.getUint32(p + 20, true);
    const nameLen = dv.getUint16(p + 28, true);
    const extraLen = dv.getUint16(p + 30, true);
    const commentLen = dv.getUint16(p + 32, true);
    const localOffset = dv.getUint32(p + 42, true);
    const name = dec.decode(new Uint8Array(buf, p + 46, nameLen));
    out.set(name, { name, method, compressedSize, localOffset });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return out;
}

async function readEntry(buf: ArrayBuffer, e: ZipEntry): Promise<string> {
  const dv = new DataView(buf);
  if (dv.getUint32(e.localOffset, true) !== 0x04034b50) {
    throw new Error(`Beschädigter ZIP-Eintrag: ${e.name}`);
  }
  const nameLen = dv.getUint16(e.localOffset + 26, true);
  const extraLen = dv.getUint16(e.localOffset + 28, true);
  const dataStart = e.localOffset + 30 + nameLen + extraLen;
  const raw = new Uint8Array(buf, dataStart, e.compressedSize);

  if (e.method === 0) return new TextDecoder('utf-8').decode(raw);
  if (e.method !== 8) throw new Error(`Nicht unterstützte ZIP-Komprimierung (${e.method}).`);

  const stream = new Blob([raw]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Response(stream).text();
}

// ------------------------------------------------------------------
// XLSX → Grid
// ------------------------------------------------------------------

function parseXml(xml: string): Document {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.querySelector('parsererror')) throw new Error('XML in der Datei ist beschädigt.');
  return doc;
}

/** "BC" → 28 (0-basiert) */
function colIndex(ref: string): number {
  const letters = /^[A-Z]+/.exec(ref.toUpperCase())?.[0] ?? 'A';
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

/** Excel-Serientag → TT.MM.JJJJ (1900-System, inkl. dessen Schaltjahr-Bug) */
function serialToDate(n: number): string {
  const ms = Math.round((n - 25_569) * 86_400_000);
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return String(n);
  const p = (x: number) => String(x).padStart(2, '0');
  return `${p(d.getUTCDate())}.${p(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`;
}

// Eingebaute Datums-/Zeitformate in OOXML.
const BUILTIN_DATE_FMTS = new Set([
  14, 15, 16, 17, 18, 19, 20, 21, 22, 45, 46, 47,
  27, 28, 29, 30, 31, 32, 33, 34, 35, 36,
  50, 51, 52, 53, 54, 55, 56, 57, 58,
]);

/** Enthält der Format-Code Datums-Platzhalter (Literale/Klammern ignoriert)? */
function isDateFormatCode(code: string): boolean {
  const stripped = code
    .replace(/"[^"]*"/g, '')   // "Text"-Literale
    .replace(/\[[^\]]*\]/g, '') // [rot], [$-409] …
    .replace(/\\./g, '');       // escapte Zeichen
  return /[dmyhs]/i.test(stripped) && !/^[#0.,%\s]*$/.test(stripped);
}

/**
 * Ermittelt, welche Zell-Stile (`s`-Attribut) ein Datumsformat haben.
 * Nötig, um Excel-Serientage zu erkennen — ein Zahlenbereich zu raten wäre
 * falsch: eine PLZ wie 4673 würde sonst zu einem Datum „konvertiert".
 */
function dateStyleIndices(stylesXml: string): Set<number> {
  const out = new Set<number>();
  try {
    const doc = parseXml(stylesXml);

    const custom = new Map<number, string>();
    for (const f of Array.from(doc.getElementsByTagName('numFmt'))) {
      const id = Number(f.getAttribute('numFmtId'));
      const code = f.getAttribute('formatCode') ?? '';
      if (Number.isFinite(id)) custom.set(id, code);
    }

    const cellXfs = doc.getElementsByTagName('cellXfs')[0];
    if (!cellXfs) return out;
    const xfs = Array.from(cellXfs.getElementsByTagName('xf'));
    xfs.forEach((xf, idx) => {
      const id = Number(xf.getAttribute('numFmtId') ?? '0');
      if (!Number.isFinite(id)) return;
      if (BUILTIN_DATE_FMTS.has(id)) { out.add(idx); return; }
      const code = custom.get(id);
      if (code && isDateFormatCode(code)) out.add(idx);
    });
  } catch { /* ohne Styles bleibt es bei „keine Datumsformate" */ }
  return out;
}

/** Liefert den Dateinamen des ersten *sichtbaren* Arbeitsblatts. */
function firstVisibleSheetPath(workbookXml: string, relsXml: string): string {
  const wb = parseXml(workbookXml);
  const rels = parseXml(relsXml);

  const relTarget = new Map<string, string>();
  for (const r of Array.from(rels.getElementsByTagName('Relationship'))) {
    relTarget.set(r.getAttribute('Id') ?? '', r.getAttribute('Target') ?? '');
  }

  for (const sh of Array.from(wb.getElementsByTagName('sheet'))) {
    const state = sh.getAttribute('state');
    if (state === 'hidden' || state === 'veryHidden') continue;
    const rid =
      sh.getAttribute('r:id') ??
      sh.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'id') ??
      '';
    let target = relTarget.get(rid) ?? 'worksheets/sheet1.xml';
    target = target.replace(/^\/?xl\//, '').replace(/^\//, '');
    return `xl/${target}`;
  }
  return 'xl/worksheets/sheet1.xml';
}

async function readXlsx(file: File): Promise<Grid> {
  const buf = await file.arrayBuffer();
  const entries = readCentralDirectory(buf);

  const get = (name: string) => entries.get(name);
  const wbEntry = get('xl/workbook.xml');
  if (!wbEntry) throw new Error('Das ist keine gültige Excel-Datei (xl/workbook.xml fehlt).');

  const relsEntry = get('xl/_rels/workbook.xml.rels');
  const sheetPath = firstVisibleSheetPath(
    await readEntry(buf, wbEntry),
    relsEntry ? await readEntry(buf, relsEntry) : '<Relationships/>',
  );

  const sheetEntry = get(sheetPath) ?? get('xl/worksheets/sheet1.xml');
  if (!sheetEntry) throw new Error('Kein Arbeitsblatt in der Datei gefunden.');

  // Shared Strings
  const sstEntry = get('xl/sharedStrings.xml');
  const shared: string[] = [];
  if (sstEntry) {
    const doc = parseXml(await readEntry(buf, sstEntry));
    for (const si of Array.from(doc.getElementsByTagName('si'))) {
      // <t> aller Runs zusammenhängen, aber <rPh> (Furigana) ignorieren
      let s = '';
      for (const t of Array.from(si.getElementsByTagName('t'))) {
        if (t.parentElement?.tagName === 'rPh') continue;
        s += t.textContent ?? '';
      }
      shared.push(s);
    }
  }

  // Zell-Stile mit Datumsformat (für Excel-Serientage)
  const stylesEntry = get('xl/styles.xml');
  const dateStyles = stylesEntry
    ? dateStyleIndices(await readEntry(buf, stylesEntry))
    : new Set<number>();

  // Sheet
  const doc = parseXml(await readEntry(buf, sheetEntry));
  const grid: Grid = [];

  for (const row of Array.from(doc.getElementsByTagName('row'))) {
    const cells = Array.from(row.getElementsByTagName('c'));
    if (cells.length === 0) continue;

    const rAttr = row.getAttribute('r');
    const rowIdxRaw = rAttr ? parseInt(rAttr, 10) - 1 : grid.length;
    // Excel-Grenzen: 1.048.576 Zeilen × 16.384 Spalten. Ohne Klemmung würde ein
    // manipulierter Bezug wie r="ZZZZZZZZZ1" die Auffüll-Schleifen unendlich laufen lassen.
    if (!Number.isFinite(rowIdxRaw) || rowIdxRaw < 0 || rowIdxRaw > 1_048_575) continue;
    const rowIdx = rowIdxRaw;
    const line: string[] = [];

    for (const c of cells) {
      const ref = c.getAttribute('r');
      const ci = ref ? colIndex(ref) : line.length;
      if (ci < 0 || ci > 16_383) continue;
      const t = c.getAttribute('t');
      let val = '';

      if (t === 'inlineStr') {
        val = Array.from(c.getElementsByTagName('t')).map((x) => x.textContent ?? '').join('');
      } else {
        const v = c.getElementsByTagName('v')[0];
        const raw = v?.textContent ?? '';
        if (t === 's') {
          val = shared[parseInt(raw, 10)] ?? '';
        } else if (raw !== '' && t !== 'str' && /^-?\d+(\.\d+)?$/.test(raw)) {
          // Zahl: nur konvertieren, wenn die Zelle laut Stil wirklich ein
          // Datumsformat hat — sonst würde z. B. eine PLZ zum Datum.
          const num = Number(raw);
          const styleIdx = Number(c.getAttribute('s') ?? '-1');
          val = dateStyles.has(styleIdx) && num > 0 && num < 2_958_466
            ? serialToDate(num)
            : raw;
        } else {
          val = raw;
        }
      }
      while (line.length < ci) line.push('');
      line[ci] = val.trim();
    }

    while (grid.length < rowIdx) grid.push([]);
    grid[rowIdx] = line;
  }

  return grid;
}

// ------------------------------------------------------------------
// CSV (Fallback, falls jemand als CSV zurückschickt)
// ------------------------------------------------------------------

function readCsvText(text: string): Grid {
  const clean = text.replace(/^﻿/, '');
  // Trennzeichen raten: Semikolon ist in AT-Excel der Default
  const head = clean.slice(0, 2000);
  const delim = (head.match(/;/g)?.length ?? 0) >= (head.match(/,/g)?.length ?? 0) ? ';' : ',';

  const grid: Grid = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;

  for (let i = 0; i < clean.length; i++) {
    const ch = clean[i];
    if (quoted) {
      if (ch === '"') {
        if (clean[i + 1] === '"') { cell += '"'; i++; } else quoted = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"') { quoted = true; continue; }
    if (ch === delim) { row.push(cell.trim()); cell = ''; continue; }
    if (ch === '\r') continue;
    if (ch === '\n') { row.push(cell.trim()); grid.push(row); row = []; cell = ''; continue; }
    cell += ch;
  }
  if (cell !== '' || row.length > 0) { row.push(cell.trim()); grid.push(row); }
  return grid;
}

// ------------------------------------------------------------------
// Public
// ------------------------------------------------------------------

/** Liest .xlsx/.xlsm oder .csv in ein Raster von Strings. */
export async function readTable(file: File): Promise<Grid> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv') || name.endsWith('.txt')) return readCsvText(await file.text());
  if (name.endsWith('.xls')) {
    throw new Error('Altes .xls-Format wird nicht unterstützt — bitte als .xlsx speichern.');
  }
  return readXlsx(file);
}
